import React, { useContext } from 'react';
import { withSuspense, FIGURE_ICONS, useModal, plural } from 'venn-utils';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  selectedBlocksState,
  studioLeftPanelOpen,
  blockDisplayHeader,
  blockInfoGraphicType,
  blockSettings,
} from 'venn-state';
import { ClosePanelButton, ConfirmationModal, StudioContext } from 'venn-components';
import { cn } from '@/lib/utils';
import { PanelHeader } from '../studio-main-left-panel/panels/components/PanelHeader';
import { Body1, Icon } from 'venn-ui-kit';
import { getDynamicIcon } from '../../logic/studioUtils';
import { useOrderedSelectedBlocks } from '../../hooks/useOrderedSelectedBlocks';

/** Content for the panel that allows users to view and manage multiple selected blocks. */
export const MultiStudioBlockConfigPanelContent = withSuspense(
  null,
  React.memo(function MultiStudioBlockConfigPanelContent() {
    const isMainLeftPanelExpanded = useRecoilValue(studioLeftPanelOpen);
    const setSelectedBlocks = useSetRecoilState(selectedBlocksState);
    const { onDeleteBlock } = useContext(StudioContext);
    const orderedSelectedBlocks = useOrderedSelectedBlocks();

    const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useModal();

    const handleDeleteAll = () => {
      orderedSelectedBlocks.forEach((blockId) => {
        onDeleteBlock(blockId);
      });

      setSelectedBlocks([]);
      closeDeleteModal();
    };

    return (
      <div className="h-full bg-white overflow-y-auto overflow-x-hidden relative border-solid border-0 border-l-[3px] border-l-venn-grey-30 border-r-solid border-r border-r-venn-grey-30">
        <div className={cn('absolute right-[2px]', isMainLeftPanelExpanded ? 'top-[14px]' : 'top-[11px]')}>
          <ClosePanelButton onClose={() => setSelectedBlocks([])} className="mr-[8px]" />
        </div>

        <div className="border-b border-x-0 border-t-0 border-solid border-venn-grey-30 h-[46px] pl-[10px] flex items-end">
          <Body1 className="inline-block">
            <div className="font-bold py-[4px]">Selected Blocks</div>
            <div className="h-[6px] w-full bg-venn-dark-blue" />
          </Body1>
        </div>
        <PanelHeader.Root>
          <PanelHeader.Text>{`${orderedSelectedBlocks.length} SELECTED`}</PanelHeader.Text>
          <div className="flex gap-[8px]">
            <PanelHeader.ActionButton label="Deselect All" onClick={() => setSelectedBlocks([])} destructive={false} />
            <PanelHeader.ActionButton label="Delete All" onClick={openDeleteModal} destructive />
          </div>
        </PanelHeader.Root>
        <div className="flex flex-col p-[12px] gap-[4px] overflow-y-auto">
          {orderedSelectedBlocks.map((blockId) => (
            <BlockListItem key={blockId} blockId={blockId} />
          ))}
        </div>

        {isDeleteModalOpen && (
          <ConfirmationModal
            destructive
            text="The configuration for these blocks will be lost permanently."
            subhead={`You are about to delete ${plural(orderedSelectedBlocks.length, {
              1: '1 block',
              other: `${orderedSelectedBlocks.length} blocks`,
            })}.`}
            onCancel={closeDeleteModal}
            onProceed={handleDeleteAll}
          />
        )}
      </div>
    );
  }),
);

const BlockListItem = React.memo(function BlockListItem({ blockId }: { blockId: string }) {
  const displayHeader = useRecoilValue(blockDisplayHeader(blockId));
  const settings = useRecoilValue(blockSettings(blockId));
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(blockId));

  const getBlockIcon = () => {
    if (infoGraphicType && infoGraphicType in FIGURE_ICONS) {
      return FIGURE_ICONS[infoGraphicType as keyof typeof FIGURE_ICONS];
    }

    return getDynamicIcon(settings); // Fallback to default icon
  };

  return (
    <div className="flex items-baseline gap-[6px] py-[4px]">
      <Icon type={getBlockIcon()} className="text-[12px]" />
      <div className="flex-1 text-[14px] font-medium text-venn-black">{displayHeader}</div>
    </div>
  );
});
